// LOGOMAN PRO — parse a user-uploaded SVG file into a custom icon.

export interface ImportedSvgIcon {
  id: string;
  name: string;
  paths: string[];
  /** Original viewBox so we can normalise to 0 0 100 100 on render. */
  viewBox: { x: number; y: number; w: number; h: number };
}

export async function importSvgAsIcon(file: File): Promise<ImportedSvgIcon> {
  const text = await file.text();
  return parseSvgString(text, file.name.replace(/\.svg$/i, ""));
}

export function parseSvgString(svg: string, name = "Custom"): ImportedSvgIcon {
  const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
  const root = doc.querySelector("svg");
  if (!root) throw new Error("Invalid SVG file");

  const vbAttr = root.getAttribute("viewBox");
  const w = parseFloat(root.getAttribute("width") || "100");
  const h = parseFloat(root.getAttribute("height") || "100");
  let viewBox = { x: 0, y: 0, w, h };
  if (vbAttr) {
    const [x, y, vw, vh] = vbAttr.split(/[\s,]+/).map(Number);
    viewBox = { x, y, w: vw, h: vh };
  }

  // Collect path data from <path d="..."/>; fold simple shapes into paths too.
  const paths: string[] = [];
  doc.querySelectorAll("path").forEach((p) => {
    const d = p.getAttribute("d");
    if (d) paths.push(d);
  });
  doc.querySelectorAll("circle").forEach((c) => {
    const cx = +c.getAttribute("cx")!, cy = +c.getAttribute("cy")!, r = +c.getAttribute("r")!;
    paths.push(`M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy} Z`);
  });
  doc.querySelectorAll("rect").forEach((r) => {
    const x = +r.getAttribute("x")! || 0, y = +r.getAttribute("y")! || 0;
    const w = +r.getAttribute("width")!, h = +r.getAttribute("height")!;
    paths.push(`M ${x} ${y} H ${x + w} V ${y + h} H ${x} Z`);
  });
  doc.querySelectorAll("polygon, polyline").forEach((poly) => {
    const pts = (poly.getAttribute("points") || "").trim();
    if (pts) paths.push(`M ${pts} ${poly.tagName.toLowerCase() === "polygon" ? "Z" : ""}`);
  });

  if (paths.length === 0) throw new Error("No drawable paths found in SVG");

  const id = "custom_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  return { id, name, paths, viewBox };
}
